/**
 * CentralFluxosPage — Central de Fluxos VSM/Office
 *
 * Lista todos os mapas de fluxo de valor cadastrados na organização.
 * Fonte: tabela `projects` (organization_id) + contagem de ações vinculadas.
 *
 * Indicadores críticos derivados do JSONB data.estado_atual.dashboardMetrics:
 *   PLT  → dashboardMetrics.leadTime   (minutos)
 *   VAT  → PLT * efficiency / 100      (estimativa: valor agregado)
 *   Eficiência → dashboardMetrics.efficiency (%)
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Search,
  Map as MapIcon,
  Factory,
  Layers,
  ChevronDown,
  Clock,
  TrendingUp,
  CheckSquare,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { EnterpriseRibbon } from '../components/layout/EnterpriseRibbon';
import { RibbonDesktopButton, desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';
import { DataGridEnterprise } from '../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../components/layout/DataGridEnterprise';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import type { ProjectStatus } from '../types/enterpriseProject';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_COLORS,
} from '../types/enterpriseProject';
import type { MapType } from '../types/common';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Dados do JSONB data.estado_atual extraídos para listagem */
interface FluxoMetrics {
  plt: number | null;       // Process Lead Time em minutos
  vat: number | null;       // Value-Added Time em minutos (PLT × eficiência)
  efficiency: number | null; // Ciclo de Eficiência 0–100
}

interface FluxoRow {
  id: string;
  name: string;
  status: ProjectStatus;
  mapType: MapType | null;
  description: string | null;
  metrics: FluxoMetrics;
  actionsTotal: number;
  actionsDone: number;
  createdAt: string;
  /** true = salvo apenas no localStorage deste navegador (não sincronizado) */
  isLocalOnly: boolean;
}

/** Shape mínimo do canvas Project armazenado em lean_workflow_projects */
interface CanvasProject {
  id: string;
  nome_projeto?: string | null;
  data_criacao?: string | null;
  status?: string | null;
  estado_atual?: {
    mapType?: MapType;
    dashboardMetrics?: {
      leadTime?: number;
      efficiency?: number;
    };
  } | null;
}

// Estrutura mínima do JSONB armazenado em projects.data
interface ProjectJsonData {
  estado_atual?: {
    mapType?: MapType;
    dashboardMetrics?: {
      leadTime?: number;
      efficiency?: number;
    };
  };
}

// Linha bruta do Supabase — campos opcionais para tolerar variações de schema
interface RawProjectRow {
  id: string;
  name: string | null;
  nome_projeto: string | null;
  status: string | null;
  description: string | null;
  created_at: string;
  /** Colunas de métricas VSM (adicionadas por database_fluxos_metrics.sql) */
  flow_type?: string | null;    // 'Industrial' | 'Office'
  plt_value?: number | null;    // PLT (Factory) ou TP (Office) em minutos
  vat_value?: number | null;    // VAT (Factory) ou TRA (Office) em minutos
  // JSONB presente apenas se o projeto foi criado pelo cloudProjectService
  data?: ProjectJsonData | null;
  [key: string]: unknown;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAP_TYPE_LABELS: Record<MapType, string> = {
  FACTORY: 'VSM Industrial',
  OFFICE:  'Lean Office',
};

const MAP_TYPE_COLORS: Record<MapType, string> = {
  FACTORY: 'bg-blue-500/10 text-blue-700 border border-blue-500/20',
  OFFICE:  'bg-violet-500/10 text-violet-700 border border-violet-500/20',
};

const STATUS_FILTER_OPTIONS: Array<{ value: ProjectStatus | ''; label: string }> = [
  { value: '',           label: 'Todos os Status' },
  { value: 'draft',      label: PROJECT_STATUS_LABELS.draft },
  { value: 'active',     label: PROJECT_STATUS_LABELS.active },
  { value: 'on_hold',    label: PROJECT_STATUS_LABELS.on_hold },
  { value: 'completed',  label: PROJECT_STATUS_LABELS.completed },
  { value: 'archived',   label: PROJECT_STATUS_LABELS.archived },
];

const MAP_TYPE_FILTER_OPTIONS: Array<{ value: MapType | ''; label: string }> = [
  { value: '',         label: 'Todos os Tipos' },
  { value: 'FACTORY',  label: 'VSM Industrial' },
  { value: 'OFFICE',   label: 'Lean Office' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMinutes(min: number | null): string {
  if (min === null || min <= 0) return '—';
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function fmtPct(v: number | null): string {
  if (v === null || v < 0) return '—';
  return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}%`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function computeEfficiency(mapType: MapType | null, plt: number | null, vat: number | null): number | null {
  if (plt === null || vat === null || plt <= 0) return null;
  if (mapType === 'FACTORY') {
    // PCE = VAT / PLT × 100
    return Math.min(Math.round((vat / plt) * 1000) / 10, 100);
  } else if (mapType === 'OFFICE') {
    // Eficiência = TP / (TP + TRA) × 100
    const total = plt + vat;
    return total > 0 ? Math.min(Math.round((plt / total) * 1000) / 10, 100) : null;
  }
  return null;
}

function deriveMetrics(raw: RawProjectRow): FluxoMetrics {
  // Prioridade: colunas DB (sincronizadas pelo canvas)
  const plt = typeof raw.plt_value === 'number' && raw.plt_value > 0 ? raw.plt_value : null;
  const vat = typeof raw.vat_value === 'number' && raw.vat_value >= 0 ? raw.vat_value : null;
  if (plt !== null && vat !== null) {
    const mapType = raw.flow_type === 'Industrial' ? 'FACTORY' : raw.flow_type === 'Office' ? 'OFFICE' : null;
    return { plt, vat, efficiency: computeEfficiency(mapType, plt, vat) };
  }

  // Fallback: JSONB data (projetos cloudProjectService legados)
  const m = raw.data?.estado_atual?.dashboardMetrics;
  if (!m) return { plt: null, vat: null, efficiency: null };
  const pltFb = typeof m.leadTime === 'number' && m.leadTime > 0 ? m.leadTime : null;
  const effFb = typeof m.efficiency === 'number' && m.efficiency >= 0 ? m.efficiency : null;
  const vatFb = pltFb !== null && effFb !== null ? Math.round(pltFb * effFb / 100 * 100) / 100 : null;
  return { plt: pltFb, vat: vatFb, efficiency: effFb };
}

/** Convert a localStorage canvas Project to the RawProjectRow shape used by deriveMetrics */
function canvasProjectToRaw(p: CanvasProject): RawProjectRow {
  const m = p.estado_atual?.mapType;
  return {
    id: p.id,
    name: p.nome_projeto ?? null,
    nome_projeto: p.nome_projeto ?? null,
    // 'master' is the canvas equivalent of 'active'
    status: p.status === 'master' ? 'active' : (p.status ?? 'draft'),
    description: null,
    created_at: p.data_criacao ?? new Date(0).toISOString(),
    data: {
      estado_atual: {
        mapType: m,
        dashboardMetrics: p.estado_atual?.dashboardMetrics
          ? {
              leadTime: p.estado_atual.dashboardMetrics.leadTime ?? 0,
              efficiency: p.estado_atual.dashboardMetrics.efficiency ?? 0,
            }
          : undefined,
      },
    },
  };
}

function toFluxoRow(
  raw: RawProjectRow,
  actionCounts: Map<string, { total: number; done: number }>,
  isLocalOnly = false,
): FluxoRow {
  // mapType: prefer JSONB data, fallback to flow_type DB column
  const mapTypeFromData = (raw.data?.estado_atual?.mapType ?? null) as MapType | null;
  const mapTypeFromCol: MapType | null =
    raw.flow_type === 'Industrial' ? 'FACTORY' :
    raw.flow_type === 'Office'     ? 'OFFICE'  : null;
  const mapType = mapTypeFromData ?? mapTypeFromCol;
  const counts = actionCounts.get(raw.id) ?? { total: 0, done: 0 };
  return {
    id: raw.id,
    name: raw.name ?? raw.nome_projeto ?? '(sem nome)',
    status: (raw.status as ProjectStatus) ?? 'draft',
    mapType,
    description: raw.description ?? null,
    metrics: deriveMetrics(raw),
    actionsTotal: counts.total,
    actionsDone: counts.done,
    createdAt: raw.created_at,
    isLocalOnly,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ProjectStatus }) {
  return (
    <EnterpriseBadge
      label={PROJECT_STATUS_LABELS[status] ?? status}
      color={PROJECT_STATUS_COLORS[status] ?? ''}
    />
  );
}

function MapTypeBadge({ type }: { type: MapType | null }) {
  if (!type) return <span className="text-slate-400 tabular-nums">—</span>;
  return (
    <EnterpriseBadge
      label={MAP_TYPE_LABELS[type]}
      color={MAP_TYPE_COLORS[type]}
    />
  );
}

function MetricCell({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
  const isEmpty = value === '—';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] uppercase tracking-widest text-slate-400 font-medium leading-none">{label}</span>
      <span className={`text-[11px] tabular-nums leading-none ${bold ? 'font-bold' : 'font-semibold'} ${isEmpty ? 'text-slate-300' : 'text-slate-700'}`}>
        {value}
      </span>
    </div>
  );
}

function ActionsBadge({ done, total }: { done: number; total: number }) {
  if (total === 0) return <span className="text-slate-300 tabular-nums text-[11px]">—</span>;
  const pct = Math.round((done / total) * 100);
  const color =
    pct === 100 ? 'text-emerald-600' :
    pct >= 50   ? 'text-blue-600'    :
                  'text-amber-600';
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-[11px] tabular-nums font-semibold leading-none ${color}`}>
        {done}/{total}
      </span>
      <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <>
      {[1, 2, 3, 4, 5].map(i => (
        <tr key={i} className="enterprise-table-row animate-pulse">
          {[80, 200, 90, 80, 160, 80].map((w, j) => (
            <td key={j} className="px-3 py-2">
              <div className="h-3 rounded-[2px] enterprise-surface-3" style={{ width: w }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const CentralFluxosPage: React.FC = () => {
  const navigate   = useNavigate();
  const orgId      = useOrganizationId();

  // ── Data state
  const [rows, setRows]           = useState<FluxoRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // ── Filters
  const [filterStatus,  setFilterStatus]  = useState<ProjectStatus | ''>('');
  const [filterMapType, setFilterMapType] = useState<MapType | ''>('');
  const [filterSearch,  setFilterSearch]  = useState('');

  // ── Abort controller for cleanup
  const abortRef = useRef<AbortController | null>(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const loadFluxos = useCallback(async () => {
    if (!orgId) return;

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setFetchError(null);

    try {
      // 1. Projects com dados mínimos para listagem (incluindo data JSONB para métricas)
      const { data: projectsRaw, error: projErr } = await supabase
        .from('projects')
        .select('*')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      if (projErr) {
        const msg = projErr.message ?? projErr.details ?? projErr.hint ?? JSON.stringify(projErr);
        console.warn('[CentralFluxosPage] projErr:', projErr);
        throw new Error(msg);
      }
      const projects = (projectsRaw ?? []) as RawProjectRow[];

      // Build action counts only when there are Supabase projects
      const projectIds = projects.map(p => p.id);

      // 2. Contagem de ações vinculadas (dois counts em paralelo)
      const [{ data: allActions }, { data: doneActions }] = projectIds.length > 0
        ? await Promise.all([
            supabase
              .from('actions')
              .select('project_id')
              .eq('organization_id', orgId)
              .in('project_id', projectIds),
            supabase
              .from('actions')
              .select('project_id')
              .eq('organization_id', orgId)
              .in('project_id', projectIds)
              .in('status', ['completed', 'done', 'closed', 'cancelled']),
          ])
        : [{ data: [] }, { data: [] }];

      // Montar mapa de contagens por project_id
      const actionCounts = new Map<string, { total: number; done: number }>();
      for (const id of projectIds) {
        actionCounts.set(id, { total: 0, done: 0 });
      }
      for (const a of (allActions ?? []) as Array<{ project_id: string | null }>) {
        if (!a.project_id) continue;
        const c = actionCounts.get(a.project_id);
        if (c) c.total += 1;
      }
      for (const a of (doneActions ?? []) as Array<{ project_id: string | null }>) {
        if (!a.project_id) continue;
        const c = actionCounts.get(a.project_id);
        if (c) c.done += 1;
      }

      // 3. Merge with localStorage canvas projects (not yet synced to Supabase)
      const supabaseIds = new Set(projects.map(p => p.id));
      let localOnlyRows: FluxoRow[] = [];
      try {
        const localRaw = localStorage.getItem('lean_workflow_projects');
        const localProjects: CanvasProject[] = localRaw ? JSON.parse(localRaw) : [];
        const notInSupabase = localProjects.filter(p => p.id && !supabaseIds.has(p.id));
        localOnlyRows = notInSupabase.map(p => toFluxoRow(canvasProjectToRaw(p), new Map(), true));
      } catch (localErr) {
        console.warn('[CentralFluxos] localStorage merge error:', localErr);
      }

      setRows([
        ...projects.map(p => toFluxoRow(p, actionCounts, false)),
        ...localOnlyRows,
      ]);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error
        ? err.message
        : (typeof err === 'object' && err !== null && 'message' in err)
          ? String((err as Record<string, unknown>).message)
          : JSON.stringify(err);
      console.warn('[CentralFluxosPage] Fetch failed:', err);
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadFluxos();
    return () => abortRef.current?.abort();
  }, [loadFluxos]);

  // Re-fetch when the tab becomes visible again (e.g., returning from MapaPage after a save)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // Always refetch when returning to this tab — catches both dirty Supabase saves
      // and canvas-only localStorage changes (which don't set the flag before this fix)
      localStorage.removeItem('wf_central_fluxos_dirty');
      loadFluxos();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadFluxos]);

  // ─── Filtered rows ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let result = rows;
    if (filterStatus)  result = result.filter(r => r.status === filterStatus);
    if (filterMapType) result = result.filter(r => r.mapType === filterMapType);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(
        r => r.name.toLowerCase().includes(q) ||
             (r.description ?? '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [rows, filterStatus, filterMapType, filterSearch]);

  // ─── Navigation ─────────────────────────────────────────────────────────────

  const openFluxo = useCallback((row: FluxoRow) => {
    navigate(`/mapa?projectId=${row.id}`);
  }, [navigate]);

  // ─── KPI strip ──────────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const total     = rows.length;
    const active    = rows.filter(r => r.status === 'active').length;
    const withMetrics = rows.filter(r => r.metrics.efficiency !== null);
    const avgEff    = withMetrics.length > 0
      ? Math.round(withMetrics.reduce((s, r) => s + (r.metrics.efficiency ?? 0), 0) / withMetrics.length)
      : null;
    const industrial = rows.filter(r => r.mapType === 'FACTORY').length;
    const office     = rows.filter(r => r.mapType === 'OFFICE').length;
    return { total, active, avgEff, industrial, office };
  }, [rows]);

  // ─── Grid columns ───────────────────────────────────────────────────────────

  const columns = useMemo((): DataGridColumn<FluxoRow>[] => [
    {
      key: 'status',
      label: 'Status',
      width: '110px',
      render: row => <StatusBadge status={row.status} />,
    },
    {
      key: 'name',
      label: 'Fluxo / Mapa',
      sortable: true,
      render: row => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[12px] font-medium text-slate-800 leading-tight truncate">{row.name}</span>
            {row.isLocalOnly && (
              <span className="shrink-0 px-1 py-px text-[8px] font-semibold uppercase tracking-wide rounded-sm bg-amber-100 text-amber-700 border border-amber-200">
                local
              </span>
            )}
          </div>
          {row.description && (
            <span className="text-[10px] text-slate-400 truncate leading-tight">{row.description}</span>
          )}
        </div>
      ),
    },
    {
      key: 'mapType',
      label: 'Tipo',
      width: '130px',
      render: row => <MapTypeBadge type={row.mapType} />,
    },
    {
      key: 'metrics',
      label: 'Indicadores Críticos',
      width: '260px',
      render: row => {
        const isOffice = row.mapType === 'OFFICE';
        const label1 = isOffice ? 'TP'  : 'PLT';
        const label2 = isOffice ? 'TRA' : 'VAT';
        return (
          <div className="flex items-center gap-4">
            <MetricCell label={label1}     value={fmtMinutes(row.metrics.plt)} />
            <MetricCell label={label2}     value={fmtMinutes(row.metrics.vat)} />
            <MetricCell label="Eficiência" value={fmtPct(row.metrics.efficiency)} bold />
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Ações',
      width: '90px',
      align: 'center',
      render: row => (
        <div className="flex justify-center">
          <ActionsBadge done={row.actionsDone} total={row.actionsTotal} />
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Criado em',
      width: '110px',
      mono: true,
      sortable: true,
      render: row => (
        <span className="text-[11px] text-slate-500 tabular-nums">{fmtDate(row.createdAt)}</span>
      ),
    },
  ], []);

  // ─── Ribbon ─────────────────────────────────────────────────────────────────

  const ribbon = (
    <EnterpriseRibbon moduleId="fluxos">
      <EnterpriseRibbon.Group>
        <RibbonDesktopButton
          icon={<RefreshCw strokeWidth={1.5} />}
          label="Atualizar"
          onClick={loadFluxos}
        />
        {selectedId && (
          <RibbonDesktopButton
            icon={<ExternalLink strokeWidth={1.5} />}
            label="Abrir Mapa"
            onClick={() => {
              const row = rows.find(r => r.id === selectedId);
              if (row) openFluxo(row);
            }}
          />
        )}
      </EnterpriseRibbon.Group>

      <EnterpriseRibbon.Filters>
        {/* Busca textual */}
        <div className="relative flex items-center">
          <Search size={11} className="pointer-events-none absolute left-2 text-slate-400 z-10" />
          <input
            type="text"
            placeholder="Buscar fluxo..."
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            className={`pl-6 pr-2 w-40 ${desktopRibbonInputClass}`}
          />
        </div>

        {/* Filtro de tipo */}
        <div className="relative flex items-center">
          <Layers size={11} className="pointer-events-none absolute left-2 text-slate-400 z-10" />
          <select
            value={filterMapType}
            onChange={e => setFilterMapType(e.target.value as MapType | '')}
            className={`pl-6 pr-6 w-40 appearance-none ${desktopRibbonInputClass}`}
          >
            {MAP_TYPE_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <ChevronDown size={10} className="pointer-events-none absolute right-2 text-slate-400" />
        </div>

        {/* Filtro de status */}
        <div className="relative flex items-center">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as ProjectStatus | '')}
            className={`px-2 pr-6 w-40 appearance-none ${desktopRibbonInputClass}`}
          >
            {STATUS_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <ChevronDown size={10} className="pointer-events-none absolute right-2 text-slate-400" />
        </div>
      </EnterpriseRibbon.Filters>
    </EnterpriseRibbon>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <ModuleLayout ribbon={ribbon}>
      {/* KPI Strip */}
      {!loading && rows.length > 0 && (
        <div className="flex items-center gap-6 px-6 py-2.5 border-b border-slate-200 bg-white shrink-0">
          <KpiStat icon={<MapIcon size={12} />}      label="Total"        value={String(kpis.total)} />
          <KpiStat icon={<TrendingUp size={12} />}   label="Ativos"       value={String(kpis.active)} highlight />
          <KpiStat icon={<Factory size={12} />}      label="Industrial"   value={String(kpis.industrial)} />
          <KpiStat icon={<Layers size={12} />}       label="Lean Office"  value={String(kpis.office)} />
          {kpis.avgEff !== null && (
            <KpiStat icon={<Clock size={12} />}      label="Efic. Média"  value={`${kpis.avgEff}%`} />
          )}
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {fetchError ? (
          <ErrorState message={fetchError} onRetry={loadFluxos} />
        ) : (
          <DataGridEnterprise<FluxoRow>
            columns={columns}
            data={filtered}
            rowKey={r => r.id}
            isLoading={loading}
            selectedKey={selectedId}
            onRowClick={r => setSelectedId(prev => prev === r.id ? null : r.id)}
            onRowDoubleClick={openFluxo}
            rowActions={row => (
              <button
                onClick={e => { e.stopPropagation(); openFluxo(row); }}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-sm bg-white shadow-sm transition-colors"
                title="Abrir canvas"
              >
                <ExternalLink size={10} />
                Abrir
              </button>
            )}
            emptyIcon={<MapIcon size={28} className="text-slate-300" />}
            emptyTitle="Nenhum fluxo cadastrado"
            emptySubtitle={
              filterStatus || filterMapType || filterSearch
                ? 'Nenhum resultado para os filtros aplicados.'
                : 'Crie um projeto com o módulo VSM ativo para visualizá-lo aqui.'
            }
          />
        )}
      </div>
    </ModuleLayout>
  );
};

// ─── Micro-components ─────────────────────────────────────────────────────────

function KpiStat({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-slate-400">{icon}</span>
      <div className="flex flex-col">
        <span className="text-[9px] uppercase tracking-widest text-slate-400 font-medium leading-none">{label}</span>
        <span className={`text-[13px] font-bold tabular-nums leading-tight ${highlight ? 'text-blue-600' : 'text-slate-700'}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <CheckSquare size={28} className="text-red-300" />
      <p className="text-[13px] font-medium text-red-600">Falha ao carregar fluxos</p>
      <p className="text-[11px] text-slate-400 max-w-sm">{message}</p>
      <button
        onClick={onRetry}
        className="mt-1 text-[11px] text-blue-600 underline hover:no-underline"
      >
        Tentar novamente
      </button>
    </div>
  );
}

export default CentralFluxosPage;
